// Visual source of truth: docs/specs/2026-09-28-avatar-figma-rework.md,
// docs/specs/2026-09-29-entity-class-workspace-avatars.md and
// docs/specs/2026-10-12-avatar-stack-spacing.md.
// Overlap, seam and cut shapes live in component-scoped custom properties;
// the 1.5px seam and the cut shapes are geometry with no token family.

@layer base-components {
    .group {
        // Override hatch (spec "Override hatch"): set these via className.
        // ds-deviation: the old IconList geometry (3px overlap, 1.5px white
        // ring outside each icon), which read tighter than Figma's 2px overlap
        // with a 1px ring inside. The seam is transparent instead of white, so
        // stacks read on any surface (spec A7).
        --avatar-group-overlap: var(--space-3);
        --avatar-group-seam: 1.5px;

        // Half the avatar size: the radius of the round cut.
        --avatar-group-r: 8px;

        display: inline-flex;
        align-items: center;
        gap: var(--space-4);

        // At 16px the old overlap hides too much of each avatar; Figma's
        // EntityGroup overlap and ring width fit.
        &[data-size="xs"] {
            --avatar-group-overlap: var(--space-2);
            --avatar-group-seam: var(--border-width);
        }

        &[data-size="sm"] {
            --avatar-group-r: 10px;
        }

        &[data-size="md"] {
            --avatar-group-r: 12px;
        }
    }

    // ds-deviation: Figma draws no class stack (spec B7). At 16px a diamond's
    // letter nearly reaches its tips, so the shared overlap would cut it.
    .group[data-size="xs"]:has([data-shape="diamond"]) {
        // stylelint-disable-next-line length-zero-no-unit -- used inside calc(), where a bare 0 is a number, not a length
        --avatar-group-overlap: 0px;
    }

    .stack {
        display: inline-flex;
    }

    // Every avatar keeps its full size. Each one with a neighbour is masked by
    // that neighbour's outer shape (circle, rounded square or rounded diamond
    // point) grown by the seam, so the gap follows its curves and the surface
    // shows through it.
    .item {
        // The neighbour's left edge, in this item's box.
        --avatar-group-edge: calc(100% - var(--avatar-group-overlap));
        --avatar-group-cut-r: calc(
            var(--avatar-group-r) + var(--avatar-group-seam)
        );
        --avatar-group-corner: calc(
            var(--radius-xs) + var(--avatar-group-seam)
        );

        display: inline-flex;

        & + & {
            margin-inline-start: calc(-1 * var(--avatar-group-overlap));
        }

        &:not(:last-child) {
            mask-image: radial-gradient(
                circle at calc(var(--avatar-group-edge) + var(--avatar-group-r))
                    50%,
                transparent var(--avatar-group-cut-r),
                black calc(var(--avatar-group-cut-r) + 0.5px)
            );
        }

        // Keep everything left of the neighbour's grown edge, plus what lies
        // outside its two grown rounded corners.
        &:has(+ .item [data-shape="square"]) {
            mask-image:
                linear-gradient(
                    to right,
                    black
                        calc(
                            var(--avatar-group-edge) - var(--avatar-group-seam)
                        ),
                    transparent 0
                ),
                radial-gradient(
                    circle at 100% 100%,
                    transparent var(--avatar-group-corner),
                    black calc(var(--avatar-group-corner) + 0.5px)
                ),
                radial-gradient(
                    circle at 100% 0,
                    transparent var(--avatar-group-corner),
                    black calc(var(--avatar-group-corner) + 0.5px)
                );
            mask-position:
                0 0,
                0 0,
                0 100%;
            mask-size:
                100% 100%,
                calc(var(--avatar-group-edge) + var(--radius-xs))
                    var(--radius-xs),
                calc(var(--avatar-group-edge) + var(--radius-xs))
                    var(--radius-xs);
            mask-repeat: no-repeat;
        }

        // ds-deviation: Figma draws no class stack (spec B7). The neighbour
        // diamond's outer tip touches its box, so its sharp corner sits
        // radius-xs * (sqrt(2) - 1) left of the edge and its rounding circle
        // is centred radius-xs right of it. Keep what lies left of its two
        // 45deg edges pushed out by the seam, plus what lies outside the grown
        // rounding circle inside the point's 90deg cone. 0.4142 is
        // sqrt(2) - 1, 0.7071 is 1 / sqrt(2).
        &:has(+ .item [data-shape="diamond"]) {
            --avatar-group-diamond-edge: calc(
                (3 * var(--avatar-group-r) - var(--avatar-group-overlap) -
                        var(--radius-xs) * 0.4142) *
                    0.7071 - var(--avatar-group-seam)
            );
            --avatar-group-diamond-centre: calc(
                var(--avatar-group-edge) + var(--radius-xs)
            );

            mask-image:
                linear-gradient(
                    135deg,
                    black calc(var(--avatar-group-diamond-edge) - 0.5px),
                    transparent var(--avatar-group-diamond-edge)
                ),
                linear-gradient(
                    45deg,
                    black calc(var(--avatar-group-diamond-edge) - 0.5px),
                    transparent var(--avatar-group-diamond-edge)
                ),
                radial-gradient(
                    circle at var(--avatar-group-diamond-centre) 50%,
                    transparent var(--avatar-group-corner),
                    black calc(var(--avatar-group-corner) + 0.5px)
                ),
                conic-gradient(
                    from 225deg at var(--avatar-group-diamond-centre) 50%,
                    black 90deg,
                    transparent 0
                );
            mask-composite: add, add, intersect, add;
        }
    }

    // First on top: each avatar after the first is cut along the previous
    // avatar's circle, grown by the seam, whose centre sits a radius left of
    // the overlap in this item's box. Circles only (presence stacks are
    // people and the Kick bot); `mask: none` drops the shape rules above.
    .group[data-stacking="firstOnTop"] .item {
        mask: none;

        &:not(:first-child) {
            mask-image: radial-gradient(
                circle at
                    calc(var(--avatar-group-overlap) - var(--avatar-group-r))
                    50%,
                transparent var(--avatar-group-cut-r),
                black calc(var(--avatar-group-cut-r) + 0.5px)
            );
        }
    }
}
